// Keyboard shortcut cheat card — shown when overlay === "keyboard".
//
// Two-column list of `keys` → `action` rows from the panel's
// shortcuts data. Dismissed via Escape, click outside, or "Close".

import { useEffect } from "react";
import { useHelp } from "./help-context";
import { getHelpData } from "./help-data";

export function KeyboardCheatCard() {
  const { panelKind, overlay, closeOverlay } = useHelp();
  const data = getHelpData(panelKind);

  useEffect(() => {
    if (overlay !== "keyboard") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeOverlay();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [overlay, closeOverlay]);

  if (overlay !== "keyboard") return null;
  if (data.shortcuts.length === 0) return null;

  return (
    <div
      role="dialog"
      aria-label="Keyboard shortcuts"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(43, 44, 47, 0.42)",
        backdropFilter: "blur(2px)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) closeOverlay();
      }}
    >
      <div
        style={{
          background: "var(--white, #ffffff)",
          border: "1px solid var(--rule, #d8d3c7)",
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: "92%",
          boxShadow: "0 12px 40px rgba(27, 22, 10, 0.2)",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--muted, #7a7363)",
            marginBottom: 12,
          }}
        >
          Keyboard shortcuts
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 8, columnGap: 18 }}>
          {data.shortcuts.map((s, i) => (
            <div key={i} style={{ display: "contents" }}>
              <span
                style={{
                  fontFamily: "var(--font-mono), ui-monospace, monospace",
                  fontWeight: 600,
                  color: "var(--ink, #1b160a)",
                  whiteSpace: "nowrap",
                }}
              >
                {s.keys}
              </span>
              <span style={{ color: "var(--body, #2b2c2f)" }}>{s.action}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={closeOverlay}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "1px solid var(--rule, #d8d3c7)",
              background: "var(--white, #ffffff)",
              color: "var(--ink, #1b160a)",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
